import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTheme } from '../components/ThemeContext';
import { useResponsive } from '../components/useResponsive';
import { API_BASE } from '../../config';

// ============================================================
// TYPES
// ============================================================

interface FailedProduct {
  ndc: string;
  product_name?: string;
  manufacturer?: string;
  coo_country?: string;
  run_id: string;
  failed_at: string;
  error_type: string;
  error_message: string;
  run_type?: string;
}

// ============================================================
// AGENT DISPLAY NAMES
// ============================================================

const AGENT_DISPLAY: Record<string, string> = {
  coo_scoring: 'COO Scoring Agent',
  ndc_lookup: 'NDC Lookup Agent',
  qrs: 'Quality Rating System',
  pis: 'Pricing Intelligence System',
  coors: 'COORS Agent',
  pns: 'Political Nexus Scoring',
};

// ============================================================
// HELPERS
// ============================================================

function formatTimestamp(iso: string): string {
  if (!iso) return '--';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '--';
  return d.toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit', hour12: true,
  });
}

function errorSeverityColor(errorType: string): string {
  switch (errorType) {
    case 'fatal': return '#EF4444';
    case 'retryable': return '#F59E0B';
    case 'unexpected': return '#EF4444';
    default: return '#94A3B8';
  }
}

// ============================================================
// COMPONENT
// ============================================================

export default function FailedScoring() {
  const { agentName } = useParams<{ agentName: string }>();
  const navigate = useNavigate();
  const { T } = useTheme();
  const { isMobile } = useResponsive();

  const [products, setProducts] = useState<FailedProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  const agentDisplay = AGENT_DISPLAY[agentName || ''] || (agentName || '').replace(/_/g, ' ');

  const fetchProducts = useCallback(async () => {
    if (!agentName) return;
    try {
      const resp = await fetch(
        `${API_BASE}/api/metasupply/swarm/agents/${agentName}/failed-products`,
        { credentials: 'include' },
      );
      if (resp.ok) {
        const data = await resp.json();
        setProducts(data.products || []);
        setError(null);
      } else {
        setError('Failed to fetch failed products');
      }
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to fetch failed products');
    } finally {
      setLoading(false);
    }
  }, [agentName]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const toggleRow = (key: string) => {
    setExpandedRows(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: 40, height: 40, border: `3px solid ${T.border}`,
            borderTopColor: T.gold, borderRadius: '50%',
            animation: 'spin 1s linear infinite', margin: '0 auto 16px',
          }} />
          <span style={{ fontSize: 14, color: T.textLight }}>Loading failed products...</span>
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>

      {/* Back link + Header */}
      <div>
        <button
          onClick={() => navigate(`/agent-detail/${agentName}`)}
          style={{
            border: 'none', background: 'none', cursor: 'pointer', fontSize: 13,
            color: T.navyLight, fontWeight: 600, padding: 0, fontFamily: 'inherit',
            display: 'flex', alignItems: 'center', gap: 6, marginBottom: 16,
          }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M19 12H5M12 19l-7-7 7-7" />
          </svg>
          Back to {agentDisplay}
        </button>

        <div style={{
          background: T.offWhite, border: `1px solid ${T.border}`,
          borderRadius: 16, padding: isMobile ? '24px 16px' : '32px 40px',
          display: 'flex', flexDirection: isMobile ? 'column' : 'row',
          justifyContent: 'space-between', alignItems: isMobile ? 'flex-start' : 'center', gap: 20,
        }}>
          <div>
            <h1 style={{ fontSize: isMobile ? 22 : 28, fontWeight: 800, color: T.textDark, margin: 0, marginBottom: 8, fontFamily: "'Playfair Display', Georgia, serif" }}>
              Failed Scoring
            </h1>
            <p style={{ fontSize: 14, color: T.textMid, maxWidth: 600, lineHeight: 1.6, margin: 0 }}>
              Products that encountered errors during rescoring by the {agentDisplay}.
              Click a row to see error details.
            </p>
          </div>
          <div style={{
            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
            background: products.length > 0 ? 'rgba(239,68,68,0.15)' : `${T.navy}08`,
            borderRadius: 12, padding: '16px 24px',
          }}>
            <span style={{ fontSize: 10, fontWeight: 700, color: products.length > 0 ? T.danger : T.red, textTransform: 'uppercase', letterSpacing: '0.12em', fontFamily: "'IBM Plex Mono', 'Courier New', monospace" }}>
              Total Failed
            </span>
            <span style={{ fontSize: 32, fontWeight: 800, color: products.length > 0 ? T.danger : T.textDark }}>
              {products.length}
            </span>
          </div>
        </div>
      </div>

      {error && (
        <div style={{
          padding: '12px 16px', borderRadius: T.radiusSm,
          background: T.dangerBg, border: `1px solid ${T.danger}30`,
          color: T.danger, fontSize: 13, fontWeight: 600,
        }}>
          {error}
        </div>
      )}

      {/* Failed Products Table */}
      <div style={{
        background: T.white, border: `1px solid ${T.border}`,
        borderRadius: T.radius, boxShadow: T.shadow, overflow: 'hidden',
      }}>
        {products.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center' }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>--</div>
            <div style={{ fontSize: 15, fontWeight: 600, color: T.success, marginBottom: 8 }}>
              No Failed Products
            </div>
            <div style={{ fontSize: 13, color: T.textLight, maxWidth: 400, margin: '0 auto' }}>
              All products have been scored successfully. Products that fail during
              the rescoring process will appear here with error details.
            </div>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ borderBottom: `2px solid ${T.border}` }}>
                  {['NDC #', 'Product Name', 'Manufacturer', 'Country of Origin', 'Error Type', 'Error', 'Failed At'].map(col => (
                    <th key={col} style={{
                      padding: '12px 16px', textAlign: 'left', fontSize: 10, fontWeight: 700,
                      color: T.textLight, textTransform: 'uppercase', letterSpacing: '0.08em',
                      whiteSpace: 'nowrap',
                    }}>
                      {col}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {products.map((product, idx) => {
                  const rowKey = `${product.ndc}-${product.run_id}-${idx}`;
                  const isExpanded = expandedRows.has(rowKey);

                  return (
                    <React.Fragment key={rowKey}>
                      <tr
                        onClick={() => toggleRow(rowKey)}
                        style={{
                          borderBottom: `1px solid ${T.borderLight}`,
                          cursor: 'pointer',
                          background: isExpanded ? `${T.danger}06` : 'transparent',
                          transition: 'background 0.15s',
                        }}
                        onMouseEnter={e => { if (!isExpanded) (e.currentTarget as HTMLTableRowElement).style.background = `${T.danger}04`; }}
                        onMouseLeave={e => { if (!isExpanded) (e.currentTarget as HTMLTableRowElement).style.background = 'transparent'; }}
                      >
                        <td style={{ padding: '12px 16px', fontWeight: 600, color: T.navy, whiteSpace: 'nowrap' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <svg
                              width="14" height="14" viewBox="0 0 24 24" fill="none"
                              stroke={T.textLight} strokeWidth="2"
                              style={{
                                transition: 'transform 0.2s',
                                transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                                flexShrink: 0,
                              }}
                            >
                              <path d="M6 9l6 6 6-6" />
                            </svg>
                            {product.ndc}
                          </div>
                        </td>
                        <td style={{ padding: '12px 16px', color: T.textDark, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {product.product_name || '--'}
                        </td>
                        <td style={{ padding: '12px 16px', color: T.textDark }}>
                          {product.manufacturer || '--'}
                        </td>
                        <td style={{ padding: '12px 16px', color: T.textDark }}>
                          {product.coo_country || '--'}
                        </td>
                        <td style={{ padding: '12px 16px' }}>
                          <span style={{
                            fontSize: 10, fontWeight: 700, textTransform: 'uppercase',
                            letterSpacing: '0.04em', padding: '3px 10px', borderRadius: 10,
                            background: `${errorSeverityColor(product.error_type)}18`,
                            color: errorSeverityColor(product.error_type),
                          }}>
                            {product.error_type}
                          </span>
                        </td>
                        <td style={{
                          padding: '12px 16px', color: T.danger, fontSize: 12,
                          maxWidth: 250, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        }}>
                          {product.error_message}
                        </td>
                        <td style={{ padding: '12px 16px', color: T.textLight, fontSize: 12, whiteSpace: 'nowrap' }}>
                          {formatTimestamp(product.failed_at)}
                        </td>
                      </tr>

                      {/* Expanded error details */}
                      {isExpanded && (
                        <tr>
                          <td colSpan={7} style={{ padding: 0 }}>
                            <div style={{
                              padding: '16px 24px 20px',
                              background: `linear-gradient(180deg, ${T.offWhite} 0%, ${T.white} 100%)`,
                              borderBottom: `2px solid ${T.danger}30`,
                            }}>
                              <div style={{
                                fontSize: 11, fontWeight: 700, color: T.textLight,
                                textTransform: 'uppercase', letterSpacing: '0.1em',
                                marginBottom: 12,
                              }}>
                                Error Details
                              </div>

                              <div style={{
                                padding: '12px 16px', borderRadius: 8,
                                background: `${T.danger}08`, border: `1px solid ${T.danger}20`,
                              }}>
                                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '120px 1fr', gap: 8 }}>
                                  <span style={{ fontSize: 11, fontWeight: 700, color: T.textLight }}>NDC:</span>
                                  <span style={{ fontSize: 13, color: T.textDark }}>{product.ndc}</span>

                                  <span style={{ fontSize: 11, fontWeight: 700, color: T.textLight }}>Error Type:</span>
                                  <span style={{ fontSize: 13, color: errorSeverityColor(product.error_type), fontWeight: 600 }}>
                                    {product.error_type.toUpperCase()}
                                  </span>

                                  <span style={{ fontSize: 11, fontWeight: 700, color: T.textLight }}>Message:</span>
                                  <span style={{ fontSize: 13, color: T.danger, wordBreak: 'break-word' }}>
                                    {product.error_message}
                                  </span>

                                  <span style={{ fontSize: 11, fontWeight: 700, color: T.textLight }}>Run ID:</span>
                                  <span style={{ fontSize: 12, color: T.textLight, fontFamily: 'monospace' }}>
                                    {product.run_id}
                                  </span>

                                  <span style={{ fontSize: 11, fontWeight: 700, color: T.textLight }}>Failed At:</span>
                                  <span style={{ fontSize: 13, color: T.textDark }}>
                                    {formatTimestamp(product.failed_at)}
                                  </span>

                                  {product.run_type && (
                                    <>
                                      <span style={{ fontSize: 11, fontWeight: 700, color: T.textLight }}>Run Type:</span>
                                      <span style={{ fontSize: 13, color: T.textDark }}>
                                        {product.run_type}
                                      </span>
                                    </>
                                  )}
                                </div>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
